import { getRegionCodeForCountryCode, parsePhoneNumber } from 'awesome-phonenumber'
import { isEmpty, reject, replace } from 'lodash'
import moment from 'moment'

import conformToMask from 'text-mask-core/src/conformToMask'
import extendMaskReplacers from 'v-mask/src/utils/extendMaskReplacers'
import parseMask from 'v-mask/src/utils/parseMask'

import { REQUIRED_PROPERTIES as PARTY_REQUIRED_PROPERTIES } from '@/store/legal-case'

export const partyName = party => {
  const name = [
    party?.first_name,
    party?.middle_name,
    party?.last_name,
  ].
    filter(t => typeof t === 'string').
    map(t => t.trim()).
    filter(t => t.length > 0).
    join(' ').
    replace(/\s+/g, ' ').
    toUpperCase()

  return name || null
}

export const partyNameInitials = party => {
  return `${party?.first_name?.[0] || ''}${party?.last_name?.[0] || ''}`.toUpperCase().trim()
}

/** ADDRESSES **/

export const findPartyAddressByType = (addresses, addressType) => {
  const address = addresses?.find(a => a.address_type === addressType)

  return address
}

// An address is considered empty if none of the fields "address", "city" and "state" contain a value
export const isPartyAddressEmpty = address => {
  return !address || (
    (address.address || '').trim().length +
    (address.city || '').trim().length +
    (address.state || '').trim().length
  ) === 0
}

function convertPartyAddressToInline (address) {
  const sections = [address.address, address.city, address.state, address.zip]
  return sections.filter(s => !!s).join(', ')
}

export const getInlinePartyAddresses = addresses => {
  const result = {
    mailingAddress: undefined,
    mailingAddressSameAsPhysical: false,
    physicalAddress: undefined,
  }

  const physical = findPartyAddressByType(addresses, 'physical')
  const mailing = findPartyAddressByType(addresses, 'mailing')

  if (!isPartyAddressEmpty(physical)) result.physicalAddress = convertPartyAddressToInline(physical)
  if (!isPartyAddressEmpty(mailing)) result.mailingAddress = convertPartyAddressToInline(mailing)
  result.mailingAddressSameAsPhysical = mailing?.same_as_physical === true

  return result
}

/** EMAILS **/

export const filterValidPartyEmails = emails => {
  return (emails || []).filter(e => (e.address || '').trim().length)
}

export const getInlinePartyEmail = emails => {
  const available = filterValidPartyEmails(emails)
  if (!available.length) return undefined

  const primary = available.find(addr => addr.primary)
  if (primary) return primary.address

  const lastAdded = available.reduce((result, entry) => {
    if (moment(entry.created_at).isAfter(result.created_at)) return entry
    return result
  })

  return lastAdded.address
}

/** PHONE NUMBERS **/

export const filterValidPartyPhones = phones => {
  return (phones || []).filter(p => (p.phone_number || '').trim().length)
}

export const sortPhonesByImportance = phones => {
  const calcPhoneWeight = phone => {
    const phoneTypeWeight = {
      inttext: 2, // non-us with texting
      intvoice: 3, // non-us voice only
      mt: 5, // mobile with texting
      mn: 6, // mobile voice only
      ll: 4, // landline
      ot: 0, // other
      wa: 1, // whatsapp
    }

    let weight = ((phone.prefferred_voice | 0) * 2) + ((phone.prefferred_text | 0) * 1)
    if (!weight) weight += phoneTypeWeight[phone.phone_type]

    return weight
  }

  return phones.sort((a, b) => calcPhoneWeight(a) - calcPhoneWeight(b))
}

export const PHONE_PREFERRED_FOR = {
  TEXT: 1,
  VOICE: 2,
}

export const findPartyPreferredPhone = (phones, preferredFor) => {
  const available = filterValidPartyPhones(phones)

  if (preferredFor === PHONE_PREFERRED_FOR.BOTH) return available.find(p => p.preferred_voice && p.preferred_text)
  if (preferredFor === PHONE_PREFERRED_FOR.VOICE) return available.find(p => p.preferred_voice)
  if (preferredFor === PHONE_PREFERRED_FOR.TEXT) return available.find(p => p.preferred_text)

  return undefined
}

export const convertPartyPhoneToInline = (phone, opts) => {
  if (!phone) return undefined

  let phoneNumber = phone.phone_number
  if (opts.formatted && phone.country_prefix) phoneNumber = formattedPhoneNumber(phone)
  if ((phone.owner || '').trim().length) return `${phoneNumber} (${phone.owner})`
  return phoneNumber
}

export const getInlinePartyPhone = (phones, opts) => {
  const available = sortPhonesByImportance(filterValidPartyPhones(phones))
  if (!available.length) return undefined
  return convertPartyPhoneToInline(available[0], opts)
}

export const getInlinePartyPreferredPhone = (phones, preferredFor, opts) => {
  const phone = findPartyPreferredPhone(phones, preferredFor)
  return convertPartyPhoneToInline(phone, opts)
}

export const getInlinePartyPreferredPhones = (phones, opts) => {
  return {
    preferredVoicePhone: getInlinePartyPreferredPhone(phones, PHONE_PREFERRED_FOR.VOICE, opts),
    preferredTextPhone: getInlinePartyPreferredPhone(phones, PHONE_PREFERRED_FOR.TEXT, opts),
    preferredVoiceAndTextPhone: getInlinePartyPreferredPhone(phones, PHONE_PREFERRED_FOR.BOTH, opts),
    otherPhone: getInlinePartyPhone(phones, opts),
  }
}

export const formattedPhoneNumber = phone => {
  const regionCode = phone.country_iso_code || getRegionCodeForCountryCode(phone.country_prefix)
  return parsePhoneNumber(phone.phone_number, { regionCode })?.
    number?.
    international
}

/** PARTY EDIT */
export const cleanMaskedNumber = maskedNumber => replace(maskedNumber, /[-_=()\s]/gm, '')

export const applyMask = (unmaskedNumber, mask) => {
  if (!mask) return unmaskedNumber

  const replacers = extendMaskReplacers()
  const textMask = parseMask(mask, replacers)

  return conformToMask(unmaskedNumber || '', textMask, { guide: false })?.conformedValue
}

export const MASKS = {
  ANUMBER: '###-###-###',
  SSNUMBER: '###-##-####',
}

/** PARTIES SLIDER */

export const convertPartyToPartiesSliderItem = (party, role, isRecordHolder, isContactParty, countriesOptions) => {
  let birthCountry = countriesOptions.find(country => country.value === party.birth_country_id)?.text
  birthCountry = birthCountry || party.birth_country_string || party.birth_country?.name || party.birth_country

  return {
    ...getInlinePartyAddresses(party.addresses),
    ...getInlinePartyPreferredPhones(party.phones, { formatted: true }),
    aNumber: party.a_number,
    birthCountry,
    birthdate: party.dob,
    email: getInlinePartyEmail(party.emails),
    fullName: partyName(party),
    gender: party.gender_string || party.gender,
    hasDocuments: party.has_documents,
    id: party.id,
    immigrationStatus: party.immigration_status,
    initials: `${party.first_name[0] || ''}${party.last_name[0] || ''}`,
    isContactParty: isContactParty,
    isECOMember: party.is_eco_member,
    isRecordHolder: isRecordHolder,
    photo: party.photo,
    primaryLanguage: party.primary_language,
    role: role,
    ssn: party.ssn,
    tags: party.tags,
    uncertainBirthdate: party.uncertain_dob,
    eoirCaseInfoSubscriptionDetail: party.eoir_case_info_subscription_detail,
  }
}

export const partiesSliderItemSorter = (a, b) => {
  // Record Holder goes first and Contact Party second
  if (a.isRecordHolder) return -1
  if (b.isRecordHolder) return 1
  if (a.isContactParty) return -1
  if (b.isContactParty) return 1
  // otherwise do simple sort
  return simplePartiesSliderItemSorter(a, b)
}

export const simplePartiesSliderItemSorter = (a, b) => {
  // sort by date of birth
  const aBirthDate = Number((a.birthdate || '99999999').replaceAll('-', ''))
  const bBirthDate = Number((b.birthdate || '99999999').replaceAll('-', ''))
  if (aBirthDate < bBirthDate) return -1
  if (aBirthDate > bBirthDate) return 1
  // and if two parties have the same birthdate, sort them by ID
  return a.id - b.id
}

export const filterValidParties = parties => {
  return reject(parties, party => {
    return (
      !!PARTY_REQUIRED_PROPERTIES.find(property => {
        return (
          (isEmpty(party[property]?.toString().trim())) ||
          (isEmpty(party.dob) && party.uncertain_dob !== true)
        )
      })
    )
  })
}

export const partyOptionLabel = function (party) {
  let result = `${partyName(party)}`
  result += ` (${party.dob ?? 'Uncertain DOB'})`

  if (this.edited.engagement_letter.notification_channel === 'text') {
    if (!isEmpty(party.primary_text_number?.phone_number)) {
      const phoneExtension = party.primary_text_number.extension ? `+${party.primary_text_number.extension} ` : ''
      result += ` (${phoneExtension}${party.primary_text_number.phone_number})`

      if (
        !['mt', 'inttext'].includes(party.primary_text_number.phone_type) &&
          party.primary_text_number.phone_type !== 'wa'
      ) {
        result += ' (Phone number not textable)'
      } else if (party.primary_text_number.phone_type === 'wa') {
        // TODO Remove once whatsapp support fully works
        result += ' (Whatsapp not supported yet)'
      }
    } else {
      result += ' (No phone number)'
    }
  }

  if (this.edited.engagement_letter.notification_channel === 'email') {
    if (!isEmpty(party.primary_email?.address)) {
      result += ` (${party.primary_email.address})`
    } else {
      result += ' (No email)'
    }
  }

  return result
}
